// The housekeeping half of the maintenance kinds, and the rows the platform hangs off:
// retention pruning, the inbound ledger and its redrive, the gardener sweep, the account /
// installation / repository / project rows, identity and SSO, the audit archive, schedules,
// the security watch and the forge observation lanes.
// Split out of `./kind-maintenance.ts` at a size budget, at the line where its work-in-flight
// kinds (tasks, sessions, janitors, plans, error issues, pull requests) end. `./kind`
// spreads this group IMMEDIATELY after that one, so the enum order is unchanged. Not in the
// barrel: this is a part of `AuditEventKind`, exported only so `./kind` can assemble it.
// Import `AuditEventKind`, never a group.

export const AUDIT_KIND_HOUSEKEEPING = {
  // #448: the 72h raw-log retention prune redacted (or failed to redact)
  // `task_run_events` payloads. Mutating retained event content is a
  // security-relevant state change, so every pass — success AND failure —
  // gets a row beyond the stdout metric line. Parent-less (cron lane), so it
  // is emitted through the worker's structured job-audit log.
  LogRetentionPruned: 'log_retention.pruned',
  // #3235: the two DAILY retention lanes that bound the platform's highest-volume
  // tables closed a pass — `cron.prune_inbound_events` and `cron.prune_outbox`.
  // Both destroy rows and both recorded the pass only on stdout, so neither kind
  // existed and nothing could ever have written one; these are the members that
  // make the lanes auditable, not a repair of a mis-binding.
  //
  // `inbound_events.pruned` names the raw webhook bodies dropped past
  // `INBOUND_EVENTS_RETENTION_DAYS` — the canonical payloads every dispatch lane
  // rehydrates from, and the replay horizon `apps/ingest`'s delivery nonce is
  // pinned to, so "what did we still hold on the day of that incident" is a
  // question only this row can answer. `outbox.pruned` names the published fleet
  // dispatches dropped past `OUTBOX_RETENTION_DAYS` — the forensic record of what
  // the mothership pushed — and carries the same pass's `fleet_run_audit_outbox`
  // obligation count, which is an AUDIT HOLE probe: a chain that wedged and healed
  // otherwise leaves nothing behind saying it ever wedged.
  //
  // ONE row per pass, on every outcome, for the reason `RunnerReleasePruned`
  // states: absence is not evidence, and a MISSING row must read as "the schedule
  // stopped" rather than "nothing aged out". (`RunnerReleasePruned` itself is one
  // CLOSING row per pass plus, on a pass that sets `runner_releases.pruned_at`, one
  // `outcome: 'marked'` row written before that one-way write, #7213; a per-tick
  // count of it filters `outcome <> 'marked'`.) Platform-anchored (a cron lane owns no
  // session), chained on the lane's own `agent_sessions` parent — not the
  // parent-less job-audit store, which only reaches stdout.
  InboundEventsPruned: 'inbound_events.pruned',
  OutboxPruned: 'outbox.pruned',
  // plan 101 09 rows 3-5: `cron.prune_platform_tables` closed a daily pass over
  // `webhook_deliveries`, dead `sessions`, `notifications` and delivered
  // `digest_queue` entries. ONE row per pass on every outcome, carrying each
  // table's window, deleted count and whether the per-run bound deferred the
  // rest — the rows are gone afterwards, so this is the only evidence of what
  // the platform dropped and when. Platform-anchored like the two above.
  PlatformTablesPruned: 'platform_tables.pruned',
  // #4400 (epic #4391, Terminal slice 09): the third of the issue's three named
  // emissions. Source and event type only, NEVER the payload — the payload
  // stays in `inbound_events`, exactly like `InboundEventsPruned` above never
  // carries the rows it deletes. Fires from `apps/ingest/src/routes/hooks.ts`'s
  // `POST /hooks/:source_id` lane, right after the connection row resolves the
  // tenant and the delivery is persisted as genuinely NEW (the same
  // `onConflictDoNothing` insert `isReplay` already gates on) — never on a
  // redelivered `delivery_id`, which is the transition this kind names.
  // NOT parent-less: like `PrIssueLinked` above, a webhook has no agent
  // session, so the row chains onto the account's `ensureGatewaySessionParent`
  // anchor via `@developerz/audit`'s `createTenantAuditSink`.
  //
  // Scoped to `/hooks/:source_id` ONLY — every connected non-GitHub source
  // (Sentry, Linear, generic), which is the one lane that resolves an account
  // AT RECEIPT (`source.accountId`, the connection row IS the tenant).
  // `/gh/webhooks` (`apps/ingest/src/index.ts`) has no such seam: a GitHub
  // delivery's tenant is decided downstream, per event kind, across several
  // consumer lanes, not at one hop this pod owns — instrumenting it is a
  // separate, larger change the issue does not describe and this kind does not
  // cover.
  InboundReceived: 'inbound.received',
  // #456: the weekly gardener — the automatic drift/update sweep over managed
  // repos + enrolled boxes. `gardener.sweep_started` brackets the tick's start;
  // `gardener.sweep_summary` closes it with the counts (repos checked / enqueued /
  // deferred-by-cap, boxes tasked / skipped). Per-repo and per-box actions land
  // through the REUSED lanes' own kinds (`mcp_provision.*`, `task.assigned`), so
  // these two only attest the sweep itself — a tick where nothing drifted is a
  // no-op beyond exactly this pair of rows. Parent-less (a cron, no session), so
  // they are emitted through the worker's structured job-audit log.
  GardenerSweepStarted: 'gardener.sweep_started',
  GardenerSweepSummary: 'gardener.sweep_summary',
  // arc 101 slice 03: the onboarding worker lane — the ONLY thing that
  // provisions tenant rows today (the GitHub App install lifecycle). Every
  // tenant-state write it makes earns a row (audit is trust — this is the lane
  // that *creates the tenant*): `account.provisioned` when the owning account is
  // upserted, `installation.linked` / `installation.revoked` when the
  // install↔account link is recorded / torn down (uninstall / suspend), and
  // `repository.discovered` / `repository.disabled` per repo added / removed.
  // `repository.enabled` has no writer since #7802 (a person's enable is `project.enabled`);
  // its rows stay readable on every chain that holds them.
  // On the account's own gateway chain: `account.provisioned` inside the identity
  // transaction (#3841), the rest through the worker's tenant sink (`DispatchAudit`,
  // #2562), which alerts and falls to its stdout belt when a durable append fails.
  // `installation.revoked` files on the account the revoke's UPDATE returns; a
  // teardown matching no `installations` row has no account and alerts instead.
  // Before #2562 all but `account.provisioned` reached only stdout.
  AccountProvisioned: 'account.provisioned',
  /**
   * #3841: an account identity write (`upsertAccountByGithubId`) found a login held
   * by a row carrying a DIFFERENT GitHub account id — the login was renamed away
   * and re-registered by someone else — and released it on the stale row to
   * `login~<id>`. Its own kind, not a field on `account.provisioned` alone: the row
   * released is a different tenant from the one provisioned, and "which tenant lost
   * its login, and why" is the question an operator asks after a rename.
   */
  AccountLoginReleased: 'account.login_released',
  /**
   * One human's DUPLICATE `users` rows folded onto the row they actually sign in
   * as (#4483), by `scripts/consolidate-duplicate-user-rows.ts`.
   *
   * Its own kind because the subject is an IDENTITY, not an account: the rows
   * arose from migration 0038's synthetic ids and an SSO link that landed on the
   * wrong row, and "which row survived, what it absorbed, and what was retired"
   * is the question asked afterwards. These rows are deleted and nothing else
   * records they existed, so the payload reconstructs each one. Its keys, exactly:
   *
   * - `input`: `{ login, keep, retire }` — the login ruled on, the surviving row's
   *   id, and the retired rows' ids.
   * - `output`: `{ adoptedEmail, stampedGithubUserId, retired }` — the email the
   *   survivor took (or `null`), the GitHub id stamped onto it (or `null`), and one
   *   entry per retired row: `{ id, login, email, githubUserId, hasSsoSubject,
   *   approvedAt, cascaded, memberships }`. `githubUserId` includes a synthetic id;
   *   `approvedAt` is an ISO string or `null`; `memberships` is the
   *   `{ accountId, role }[]` its DELETE returned; `cascaded` is the COUNT of rows
   *   under each cascading foreign key other than memberships, keyed `table.column`,
   *   a key absent where there were none. Counts, not contents: a cascaded session
   *   or notification is recorded as having existed, not restored.
   *
   * Every email lands as `[REDACTED:email]` (or `null` where there was none): the
   * audit store scrubs payload strings (`docs/idea/audit.md` § Redaction), so the
   * row records THAT a retired row had an address, not which. An adopted address
   * lives on in the survivor's `users.email`.
   */
  UserRowsConsolidated: 'user.rows_consolidated',
  AccountLoginRenamed: 'account.login_renamed',
  /**
   * #3841: an account identity write REFUSED — the login is held by a row with no
   * `github_account_id` yet, so nothing proves whether the caller is that tenant or
   * a re-registration of its old login. Nothing was written; the backfill
   * (`scripts/backfill-account-github-ids.ts`) is the remedy, and the kind (with its
   * branch) is deleted once the column goes NOT NULL.
   */
  AccountIdentityRefused: 'account.identity_refused',
  /**
   * #3841: `scripts/backfill-account-github-ids.ts --apply` stamped a NULL
   * `accounts.github_account_id` from App-authenticated installation evidence (every
   * live installation agreeing on one id), never from a login lookup. One row per
   * successful compare-and-swap, on the stamped account's own gateway chain,
   * carrying the installations and ids seen — stamping a tenant's identity key is the
   * write a takeover would make, so it is never an unlogged one.
   */
  AccountGithubIdBackfilled: 'account.github_id_backfilled',
  /**
   * #3841: `scripts/seed-org-sso.ts` (`seedOrgIdentityProvider`) enabled custom SSO
   * on an org and created or replaced its IdP connection — an authentication trust
   * anchor. Written in the SAME transaction as the write, on the platform chain
   * `seed:org-sso`, naming the account by its GitHub id; the sealed config is never
   * in the row.
   */
  AccountOrgIdpSeeded: 'account.org_idp_seeded',
  /**
   * Plan 102 slice 06 (#7545): an owner or admin set the account's maintenance
   * posture (`accounts.maintain_posture`, `standard` | `maintain`) through the one
   * audited route. Appended on the SAME transaction as the write
   * (`setAccountPosture`, `@developerz/db`), one row per set — a no-op set
   * included, so "who asked and when" stays answerable. `output` is
   * `{ before, after }` as that write answers it.
   */
  AccountPostureChanged: 'account.posture_changed',
  InstallationLinked: 'installation.linked',
  InstallationRevoked: 'installation.revoked',
  /**
   * Plan 101 slice 03 door 3 (#7298): the App PERMISSION MAP stored on an installation
   * (`installations.permissions`) MOVED. Two observers write the column through the one
   * `recordInstallationPermissions` query — the `installation` webhook's `created` /
   * `new_permissions_accepted` arms (`apps/worker/src/consumers/onboarding.ts`) and the
   * reconcile's `GET /app/installations/{id}` read
   * (`apps/worker/src/consumers/installation-reconcile.ts`) — and both audit through
   * `observeInstallationPermissions` (`apps/worker/src/consumers/installation-permissions.ts`),
   * so this row has ONE spelling. Written only when the write's own pre-image differs
   * (`changed` is decided in SQL, `RETURNING old.*`), so a redelivery or a converged
   * sweep audits nothing; a FIRST observation carries `before: null`. `input` is
   * `{ installationId, accountId, source, deliveryId? }` (`source`: `installation_webhook`
   * | `app_read`); `output` is `{ before, after }`, the two maps VERBATIM (GitHub's slugs
   * as keys), so the move can be read back without GitHub. Tenant-anchored on the
   * installation's account and `visibleToMaintainer`: the `repo_ci_gate` rung reads
   * `workflows: write` off this column, and the owner's click that grants it is the
   * transition this row records.
   */
  InstallationPermissionsChanged: 'installation.permissions_changed',
  RepositoryEnabled: 'repository.enabled',
  /**
   * The account stopped operating a repo, on that account's chain. `input.source` names
   * the writer (#5962): `installation_removed`, `git_token_self_heal`, `ops_orphan_sweep`
   * or `transferred_out` (another account took the row: the install upsert or the
   * operator's light switch, #6248). Every source carries `cancelledTaskIds`; the
   * account's own switch-off is `project.disabled`, not this kind.
   *
   * A `transferred_out` row's `input` is `{ githubRepoId, fullName, deliveryId,
   * source, cancelledTaskIds, purgedSecrets }` (`transferredOutAuditOnTx`,
   * `apps/worker/src/consumers/onboarding-repo-leave.ts`): `fullName` is the LEAVING
   * account's name for the repo, `deliveryId` is `null` from the light switch, and
   * `purgedSecrets` is `{ credentialVault, projectIngress, repoSecrets }`, the COUNT of the
   * leaving account's rows the move deleted, never a name or a value. `purgedSecrets` is
   * an ADDITIVE key from #3843: a `transferred_out` row written before it has none, which
   * means "not recorded", not zero. No reader keys on it, and the row hash covers the
   * stored `input` as written, so older rows still verify.
   */
  RepositoryDisabled: 'repository.disabled',
  // #2270 (the note above `TaskCancelRefused`): promoted from `mcp_tool.call.*`.
  // The account's own project switch (`deps/repos.ts`, `deps/project-control.ts`),
  // distinct from the installation-driven `repository.disabled` above.
  ProjectEnabled: 'project.enabled',
  ProjectDisabled: 'project.disabled',
  // A person took a recorded approve or skip back (`apps/api/src/deps/pr-undo-decision.ts`).
  PrDecisionUndone: 'pr.decision_undone',
  ProjectEnableRefused: 'project.enable_refused',
  // #6124 (the #2270 note above `TaskCancelRefused`): the account sites #2270 left on
  // `mcp_tool.call.*`, each value the name the site wrote in `tool`. A name that was
  // written as BOTH ok and err got an err sibling, so the kind carries the verdict:
  // `admin.provision` / `admin.provision_partial`, `repo_binding.user_token_bind` /
  // `repo_binding.user_token_bind_refused`. The #2270 note's one query is WRONG for
  // these: old err rows carry the BASE name in `tool`. History reads:
  //   base:    kind = <base> OR (kind = 'mcp_tool.call.ok' AND tool = <base>)
  //   sibling: kind = <sibling> OR (kind = 'mcp_tool.call.err' AND tool = <base>)
  // The project trails (`deps/greenfield.ts`, `deps/project-setup.ts`): a greenfield
  // project created, or its record / scaffold dispatch / scaffold plan failed; and an
  // existing repo's setup plan handed to dispatch, or not planned.
  ProjectCreated: 'project.created',
  ProjectRecordFailed: 'project.record_failed',
  ProjectScaffoldDispatchFailed: 'project.scaffold_dispatch_failed',
  ProjectScaffoldPlanFailed: 'project.scaffold_plan_failed',
  ProjectSetupDispatched: 'project.setup_dispatched',
  ProjectSetupPlanFailed: 'project.setup_plan_failed',
  // A repo's canary switch (`deps/repos.ts`), and a user-token repo binding made,
  // refused or removed (`deps/user-token-binding.ts`).
  RepoCanaryEnabled: 'repo.canary_enabled',
  RepoCanaryDisabled: 'repo.canary_disabled',
  // A repo's public-stats consent set or withdrawn (`deps/repos.ts`, #5660).
  RepoPublicStatsEnabled: 'repo.public_stats_enabled',
  RepoPublicStatsDisabled: 'repo.public_stats_disabled',
  // A repo's platform-side merge hold set or cleared (`deps/repo-merge-override.ts`, #7011),
  // and a clear refused because the file itself does not auto-merge (409).
  RepoMergeOverrideSet: 'repo.merge_override_set',
  RepoMergeOverrideRefused: 'repo.merge_override_refused',
  // A squad (#7010, `apps/api/src/deps/squads.ts`): an admin-named group of repos created,
  // renamed, re-membered or deleted, each in the write's own transaction; and a write
  // refused with a 409 / 422 (`squad_name_taken`, `repo_in_another_squad`,
  // `squad_name_invalid`), which changed nothing.
  SquadCreated: 'squad.created',
  SquadRenamed: 'squad.renamed',
  SquadReposSet: 'squad.repos_set',
  SquadDeleted: 'squad.deleted',
  SquadWriteRefused: 'squad.write_refused',
  RepoBindingUserTokenBind: 'repo_binding.user_token_bind',
  RepoBindingUserTokenBindRefused: 'repo_binding.user_token_bind_refused',
  RepoBindingUserTokenUnbind: 'repo_binding.user_token_unbind',
  // The onboarding funnel: a step advanced (the api's `index.ts` and the worker's
  // `consumers/onboarding-advance-port.ts`, one spelling), and the plan-intent pair (#1042).
  OnboardingAdvanced: 'onboarding.advanced',
  OnboardingIntentPersisted: 'onboarding.intent_persisted',
  OnboardingIntentPersistFailed: 'onboarding.intent_persist_failed',
  // Sign-in and identity: a session minted (`deps/session-mint.ts`); the SSO link trail
  // (`deps/sso-login.ts`, `deps/sso-identity-audit.ts`: a subject linked, a link refused
  // fail-closed on a collision, a role raised, a login refused for foreign memberships);
  // an org's SSO connection set or removed (`deps/org-sso.ts`); a personal GitHub token
  // connected or cleared (`routes/me-github-token.ts`); `/v1/me`'s account switch and
  // ToS acceptance (`routes/me.ts`); the declared region and the separate cross-border
  // consent granted or withdrawn (`routes/me-consents.ts`, #5881).
  AuthLogin: 'auth.login',
  // A sign-in by the human already signed in on the browser WIDENED that session
  // (#466): an org's pin added, or GitHub unpinning it (`deps/session-mint.ts`,
  // the link callback). Ids only: the session, the human, the pin or `unpinned`.
  AuthSessionReachExtended: 'auth.session_reach_extended',
  SsoUserLinked: 'sso.user_linked',
  SsoLinkCollision: 'sso.link_collision',
  SsoRoleElevated: 'sso.role_elevated',
  SsoLinkRefusedForeignMemberships: 'sso.link_refused_foreign_memberships',
  // One user, many logins (#466): a login JIT-created its user; a session's user
  // re-linked an identity it already held; proof-of-both merged another user in;
  // an identity was unlinked (`@developerz/db` `IdentityAuditEvent`, written inside
  // the identity transaction by `deps/sso-identity-audit.ts`).
  SsoUserCreated: 'sso.user_created',
  SsoIdentityRelinked: 'sso.identity_relinked',
  SsoUsersMerged: 'sso.users_merged',
  SsoIdentityUnlinked: 'sso.identity_unlinked',
  // One human, two users, bound by EMAIL PROOF (#7426): the signed-in user asked for a
  // link to another user with the same verified email (a mail went out); the confirm
  // merged the other user in; or the confirm refused, decided inside the merge's
  // transaction (`@developerz/db` `mergeUsersByEmailProof`, `routes/me-link-requests.ts`).
  IdentityLinkRequested: 'identity.link_requested',
  IdentityUsersMerged: 'identity.users_merged',
  IdentityLinkRefused: 'identity.link_refused',
  OrgSsoSet: 'org_sso.set',
  OrgSsoRemoved: 'org_sso.removed',
  OrgSsoRemovedNoop: 'org_sso.removed_noop',
  GithubTokenConnected: 'github_token.connected',
  GithubTokenDisconnected: 'github_token.disconnected',
  AccountActiveAccountSwitch: 'account.active_account_switch',
  UserTosAccepted: 'user.tos_accepted',
  UserRegionSet: 'user.region_set',
  UserConsentGranted: 'user.consent_granted',
  UserConsentWithdrawn: 'user.consent_withdrawn',
  // Staff on a user (`routes/admin-invitations.ts`): approved or suspended; and the
  // one-call provision (`deps/provision.ts`), whole or partial (a step refused).
  UserApproved: 'user.approved',
  UserSuspended: 'user.suspended',
  AdminProvision: 'admin.provision',
  AdminProvisionPartial: 'admin.provision_partial',
  /**
   * An install delivery or the installation reconcile (#2540, #7802) created a
   * `repositories` row for a repo we had never seen — DORMANT (`enabled = false`), so it is neither
   * `repository.enabled` (nothing was turned on) nor `repo.requires_upgrade`
   * (no tier question was asked). A repo that predates the App install gets no
   * webhook that could mint its row, so this is the only audit row that will
   * ever attest its arrival, and it must not claim the bot started operating
   * it: enabling stays an explicit human act through `PATCH /v1/repos/:o/:r`,
   * which raises `project.enabled` at that moment.
   */
  RepositoryDiscovered: 'repository.discovered',
  /**
   * The installation reconcile (#2540) listed a repo it could NOT create a row
   * for: `repositories_forge_repo_id_idx` is keyed on `(forge, instance, github_repo_id)`,
   * so the id already belongs to another account's `(forge, instance)` pair and
   * `ON CONFLICT DO NOTHING` skipped it. The installation covers the repo and this account
   * will never see it.
   *
   * Its own kind, not a field on `repository.discovered`: nothing was
   * discovered, and filing a conflict under a creation would make both
   * unqueryable. Detection only — deciding which account owns a repo is not
   * that lane's call, so the row exists to make "has this ever happened?"
   * answerable rather than to authorize a repair.
   */
  RepositoryClaimConflict: 'repository.claim_conflict',
  /**
   * The repo was marked AI-FIRST (`repositories.ai_first_at`), which is what
   * releases the claim gate's hold on every `outcome_kind = 'pr'` task on it
   * (`docs/idea/repo-onboarding.md`). Three writers reach this row — a setup
   * pass's `ready` verdict, the `dz/onboarding` pull request merging, and a
   * maintainer declaring it over `PATCH /v1/repos/:o/:r` — so the row is the one
   * place that answers "who decided this repo was ready, and when".
   *
   * Written only when the stamp ACTUALLY landed: the marker is first-stamp-wins,
   * and a redelivery re-announcing a transition it did not cause is exactly the
   * lie an audit trail must not carry.
   */
  RepoAiFirstMarked: 'repo.ai_first_marked',
  // arc 101 slice 01: `repositories.private` is an ENTITLEMENT fact (the free tier
  // covers your own PUBLIC repos), so a move of it is a billing-relevant state
  // change, not a cosmetic one — and the truth lives on GitHub, so only a delivery
  // or a re-sync can tell us it moved.
  //   `repo.visibility_changed` — one row per ACTUAL flip. Both writers (the
  //     `repository` `publicized`/`privatized` webhook and the installation
  //     visibility backfill) go through the same CAS, so a redelivery or a re-sync
  //     that changes nothing audits nothing, and the row carries which lane moved it.
  //   `repo.requires_upgrade` — the flip took an ENABLED repo private. We
  //     deliberately do NOT auto-disable (destructive, and the maintainer may be
  //     mid-upgrade) and in-flight work finishes, so nothing else in the system
  //     records that the repo's entitlement question just changed: this row plus its
  //     notification ARE the trail. Whether that private repo may still draw NEW work
  //     is the dispatch gate's call (tier × visibility), not this row's.
  // Durable on the repo's account gateway chain: both visibility writers through
  // `stampRepositoryVisibility`'s store + head port (the webhook's `lifecycleAudit`,
  // the backfill's `anchored` arm), the install path's private-repo refusal through
  // the worker's tenant sink (`DispatchAudit`, #2562).
  RepoVisibilityChanged: 'repo.visibility_changed',
  RepoRequiresUpgrade: 'repo.requires_upgrade',
  // #5647: two or more of the repo's App-authored pull requests were reverted inside
  // 24 h, and the platform auto-paused its own worker mode on the repo. At most one
  // row per trip (none if every append attempt fails), written by `prProjectionSink.pauseOnRevert`; a webhook redelivery of
  // the same revert writes nothing (the `reverted_by_pr_number` guard).
  AgentModeAutoPaused: 'agent_mode.auto_paused',
  // plan 101 slice 06 I5: GitHub renamed or transferred the repo, and
  // `repositories.full_name` followed it (keyed on the immutable
  // `github_repo_id`). Every resolver keys on `full_name`, so a rename that never
  // reached the DB left the repo looking unknown — policy unresolved, repo
  // disabled. One row per ACTUAL rename (a CAS: a redelivery writes nothing),
  // from `repository.renamed|transferred` or an `installation_target` org rename.
  RepoRenamed: 'repo.renamed',
  // …and the renames it REFUSED: a `repository.transferred` (the repo left this
  // tenant's account, so renaming it in place would hand one tenant's row another
  // owner's name) and an `installation_target.renamed` for an installation we hold
  // no live account for (it must not mint an `accounts` row). Platform-anchored:
  // the second case has no tenant to anchor on.
  RepoRenameRefused: 'repo.rename_refused',
  // plan 101 slice 06 I1/I2: a webhook delivery that never became work is now
  // either redriven or on the record.
  //   `inbound.terminally_failed` — a `gh.*` dispatch job spent its whole retry
  //     ladder; the `inbound_events` row is marked so `cron.inbound_redrive`
  //     re-enqueues it. Before this, the job was lost after ~90 s with no trace.
  //   `inbound.redriven` — the redrive cron re-enqueued a terminally failed row
  //     (after winning the row's CAS, so a concurrent pass never doubles it).
  //   `inbound.redelivery_requested` — the redrive cron asked GitHub to redeliver
  //     a delivery GitHub recorded as failed (5xx / timeout) that never became a
  //     processed `inbound_events` row. GitHub does not retry on its own.
  // Platform-anchored (the lane owns no session; the row is about a DELIVERY).
  InboundTerminallyFailed: 'inbound.terminally_failed',
  InboundRedriven: 'inbound.redriven',
  InboundRedeliveryRequested: 'inbound.redelivery_requested',
  // plan 101 10-12: `inbound.orphaned` — the redrive cron found a row the dispatch
  // lane was handed (`dispatch_lane`) that no job holds any more and nothing
  // settled: the queue state behind it was lost (a Dragonfly wipe or a snapshot
  // rewind). The row is marked failed so the redrive ladder recovers it.
  InboundOrphaned: 'inbound.orphaned',
  // plan 101 slice 06 I8: a best-effort dispatch sink (projection, babysit,
  // command, reply, link, ack, ...) threw inside `handleGhJob`. The job still
  // completes — one sink's fault must not strand the maintainer lane — but the
  // fault is no longer a `console.error` alone: this row is what tells "the lane
  // broke" apart from "the webhook never arrived".
  DispatchSinkFaulted: 'dispatch.sink_faulted',
  // plan 101 slice 05 D2 (owner ruling 2026-09-21: archive to R2, then prune past
  // a written window). `audit.archived` — one verified object (re-read and
  // checksummed) holding a keyset batch of `audit_events` / `task_run_events`
  // rows, written BEFORE the watermark that licenses their delete moves.
  // `audit.archive_pruned` — one row per pass of the delete that follows, on every
  // outcome, for the reason `inbound_events.pruned` gives: a missing row must read
  // as "the schedule stopped", never "nothing aged out".
  AuditArchived: 'audit.archived',
  AuditArchivePruned: 'audit.archive_pruned',
  // #5363: a `.maintainer.yml` `schedules:` entry came due and the hourly
  // `cron.schedules` lane (`apps/worker/src/cron/schedules.ts`) acted on it.
  //   `schedule.fired` — one `return_context` scout task per covered repo was
  //     minted (at least one fresh). Input `{ anchor, name, slot, brief_path,
  //     brief_sha, task_ids }`: the sha is the brief's blob at default-branch HEAD,
  //     so the checkup the scout ran is re-readable after the file moves on.
  //   `schedule.refused` — the slot was due and NOTHING was minted. Input
  //     `{ anchor, name, slot, kind, ... }`, `kind` naming why (`policy_unknown`,
  //     `brief_missing`, `brief_too_large`, `no_installation`, `no_scout_capacity`,
  //     `repo_not_enabled`, `repo_is_anchor`, `previous_in_flight` — the previous
  //     firing still has a task in motion), `repo` naming the listed repo on the
  //     two repo arms. A due checkup that silently did not run is the failure: the
  //     maintainer opted in and would read the silence as "all clear".
  // Tenant-anchored and `visibleToMaintainer`: both are about one account's repo.
  ScheduleFired: 'schedule.fired',
  ScheduleRefused: 'schedule.refused',
  // #7789, the security watch (`apps/worker/src/security-watch/`), all on the org's own chain:
  //   - `filed`: a first-tier advisory matched this repo. Carries running vs fixed and the
  //     urgency, so "a KEV ask jumped the queue" is on the record with why.
  //   - `raised`: a KEV listing landed on an ask already filed.
  //   - `deferred`: a person's open pull request in the same area parked an ordinary fix.
  //   - `patched`: a merged pull request closed it; the only way a row reads patched.
  SecurityWatchFiled: 'security_watch.filed',
  SecurityWatchRaised: 'security_watch.raised',
  SecurityWatchDeferred: 'security_watch.deferred',
  SecurityWatchPatched: 'security_watch.patched',
  // Plan 101 slice 05 (#5427): the intake screen read ONE staff bug report
  // (`platform_bugs`) on the worker's screening lane. EVERY attempt writes one — `ok`,
  // `discard` and `unavailable` (the classifier could not answer; the row stays
  // `screening` and `failures` counts toward the lane's escalation bound). Platform-
  // chain: the lane owns it, not a tenant — and it is HERE, not a
  // `platform_audit_events` row, because that table's `actor_user_id` names a person
  // and the screen is a machine act. Input `{ bugId }`; output `{ verdict, reason,
  // prompt_version, cached, failures }` — never the title or body.
  PlatformBugScreened: 'platform_bug.screened',
  // Plan 101 slice 07: the worker re-read ONE gitlab.com merge request named by a
  // connected `gitlab` webhook source's hint, through the GitLab REST client with the
  // source's own API token, and graded it. READ-ONLY: nothing was written back to
  // GitLab; this row is the whole effect. Tenant-anchored (`audit: { kind: 'tenant' }`,
  // the gateway session per account per month) and `visibleToMaintainer`. Output:
  // `ForgeMrObservedOutputSchema` (`./payload`) — path, iid, head sha, merge verdict,
  // CI verdict, approvals; never the token, never the delivery payload (that stays in
  // `inbound_events`).
  ForgeMrObserved: 'forge.mr.observed',
  // Plan 101 slice 07: the same lane did NOT observe the merge request a hint named,
  // for a reason a retry cannot change — the token was rejected (401) or lacks access
  // (403), the MR or project is gone (404), or the source is missing, disconnected,
  // re-pointed or holds no readable token. Without this row a maintainer whose token
  // expired sees observations simply stop. One row per refused hint, tenant-anchored and
  // `visibleToMaintainer`. Output: `ForgeMrObserveRefusedOutputSchema` (`./payload`) —
  // source id, project id, MR iid and the closed reason; never the token. A transient
  // failure (5xx, network, a 429 past the client's ladder) is NOT this row: it throws
  // for the lane's retry ladder.
  ForgeMrObserveRefused: 'forge.mr.observe_refused',
  // Plan 101 slice 10b: the Gitee twin of `forge.mr.observed`. The worker re-read ONE
  // gitee.com pull request named by a connected `gitee` webhook source's hint, through the
  // Gitee v5 client with the source's own personal access token. READ-ONLY: nothing was
  // written back to Gitee. Its own kind, not a widened `forge.mr.observed` (whose strict
  // output is GitLab-shaped). Tenant-anchored and `visibleToMaintainer`. Output:
  // `ForgePrObservedOutputSchema` (`./payload`) — `forge: 'gitee'`, path, number, head sha,
  // state / mergeable / draft, CI verdict, reviewer and tester accept counts; never the
  // token, never the delivery payload.
  ForgePrObserved: 'forge.pr.observed',
  // Plan 101 slice 10b: the same lane did NOT observe the PR a hint named, for a reason a
  // retry cannot change — the token was rejected (401) or lacks access (403), the repo is
  // gone (404 `Not Found Project`) or the PR is (404 `Pull Request not found`), or the
  // source is missing, disconnected, re-pointed or holds no readable token. One row per
  // refused hint, tenant-anchored and `visibleToMaintainer`. Output:
  // `ForgePrObserveRefusedOutputSchema` (`./payload`). A transient failure (5xx, network,
  // timeout, Gitee's 401-shaped rate limit) is NOT this row: it throws for the retry ladder.
  ForgePrObserveRefused: 'forge.pr.observe_refused',
} as const;
